'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook for managing intersection observer to detect when a div container is visible
 */
export const useContainerVisibility = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const initializeObserver = useCallback(() => {
    if (observerRef.current) return observerRef.current;

    const options = {
      root: null,
      rootMargin: '100px',
      threshold: [0, 0.1, 0.5, 1.0],
    };

    observerRef.current = new IntersectionObserver((changes) => {
      const change = changes[0];
      if (!change) return;
      // Consider visible if any part is intersecting
      if (change.isIntersecting && change.intersectionRatio > 0) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    }, options);

    return observerRef.current;
  }, []);

  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (containerRef.current && observerRef.current) {
        observerRef.current.unobserve(containerRef.current);
      }

      containerRef.current = node;

      if (!node) return;
      const observer = initializeObserver();
      observer.observe(node);

      // Required fallback check for initial visibility + already visible cases
      setTimeout(() => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInViewport) setIsVisible(true);
      }, 100);
    },
    [initializeObserver],
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { containerRef: setContainerRef, isContainerVisible: isVisible };
};
